
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, MessageSquare, Maximize, Play, Menu } from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
  currentSlide: number;
  totalSlides: number;
  onNext: () => void;
  onPrev: () => void;
  onGoTo: (index: number) => void;
  speakerNotes: string;
}

const Layout: React.FC<LayoutProps> = ({ 
  children, 
  currentSlide, 
  totalSlides, 
  onNext, 
  onPrev, 
  onGoTo,
  speakerNotes 
}) => {
  const [showNotes, setShowNotes] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  
  const progress = ((currentSlide + 1) / totalSlides) * 100;

  return (
    <div className="h-screen w-screen flex flex-col bg-black overflow-hidden relative gradient-bg">
      {/* Top Navigation Bar */}
      <header className="h-20 border-b border-white/10 flex items-center justify-between px-8 z-50 bg-black/50 backdrop-blur-md">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center font-black italic">CF</div>
          <span className="font-bold text-xl tracking-tighter uppercase">Clubfluence <span className="text-purple-500">Deck</span></span>
        </div>

        <div className="flex items-center gap-3">
          <button 
            onClick={() => setShowNotes(!showNotes)}
            className={`p-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium ${showNotes ? 'bg-purple-600 text-white' : 'hover:bg-white/10 text-gray-400'}`}
          >
            <MessageSquare size={18} />
            Notes
          </button>
          <button 
            onClick={() => document.documentElement.requestFullscreen()}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-400 transition-colors"
          >
            <Maximize size={18} />
          </button>
          <div className="w-px h-6 bg-white/10 mx-2"></div>
          <div className="relative">
             <button 
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 rounded-lg hover:bg-white/10 text-gray-400 transition-colors flex items-center gap-2"
              >
                <Menu size={18} />
                <span className="text-sm">{currentSlide + 1} / {totalSlides}</span>
              </button>
              {showMenu && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-gray-900 border border-white/10 rounded-xl shadow-2xl overflow-y-auto max-h-[70vh] z-[100]">
                  {Array.from({length: totalSlides}).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => { onGoTo(i); setShowMenu(false); }}
                      className={`w-full text-left px-4 py-3 text-sm hover:bg-white/5 transition-colors border-b border-white/5 last:border-none ${i === currentSlide ? 'text-purple-400 font-bold bg-purple-400/10' : 'text-gray-400'}`}
                    >
                      Slide {i + 1}
                    </button>
                  ))}
                </div>
              )}
          </div>
        </div>
      </header>

      {/* Main Slide Content Area */}
      <main className="flex-1 relative">
        {children}
      </main>

      {/* Footer Navigation Controls */}
      <footer className="h-20 px-8 flex items-center justify-between relative bg-black/50 backdrop-blur-sm">
        {/* Progress Bar */}
        <div className="absolute top-0 left-0 h-1 bg-white/5 w-full">
           <div className="h-full bg-purple-500 transition-all duration-300" style={{ width: `${progress}%` }}></div>
        </div>

        <div className="text-xs text-gray-500 font-bold uppercase tracking-[0.2em]">
          Investor Presentation 2025 • Strict Confidentiality
        </div>

        <div className="flex items-center gap-4">
          <button 
            onClick={onPrev}
            disabled={currentSlide === 0}
            className="w-12 h-12 flex items-center justify-center rounded-full border border-white/10 hover:bg-white/5 disabled:opacity-20 transition-all group"
          >
            <ChevronLeft size={24} className="group-active:scale-90 transition-transform" />
          </button>
          <button 
            onClick={onNext}
            disabled={currentSlide === totalSlides - 1}
            className="w-12 h-12 flex items-center justify-center rounded-full bg-purple-600 hover:bg-purple-500 disabled:opacity-20 transition-all group shadow-lg shadow-purple-500/20"
          >
            <ChevronRight size={24} className="group-active:scale-90 transition-transform" />
          </button>
        </div>
      </footer>

      {/* Speaker Notes Sidebar Overlay */}
      {showNotes && (
        <div className="absolute top-20 right-0 bottom-20 w-[320px] bg-gray-900/95 border-l border-white/10 p-8 shadow-2xl z-[60] backdrop-blur-xl animate-in slide-in-from-right duration-300">
           <div className="flex items-center gap-2 mb-6 text-purple-400">
              <MessageSquare size={16} />
              <span className="text-xs font-black uppercase tracking-widest">Speaker Notes</span>
           </div>
           <p className="text-lg text-gray-200 leading-relaxed italic">
             "{speakerNotes}"
           </p>
        </div>
      )}
    </div>
  );
};

export default Layout;
